import { BarChart,  Smartphone, Monitor, Zap, Wallet,Bot, Globe, MousePointerClick } from 'lucide-react';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    PieChart, 
    Pie 
  } from 'recharts';

export default function AdvertiserDashboard() {
  // Datos de ejemplo (debes reemplazar con tu data real)
  const metrics = {
    totalClicks: 24500,
    totalImpressions: 150000,
    averageCTR: 2.4,
    totalBudget: 50000,
    engagementRate: 18.7,
    activeCampaigns: 5,
    deviceDistribution: [
      { name: 'Mobile', value: 65 },
      { name: 'Desktop', value: 30 },
      { name: 'Bot', value: 5 }
    ],
    performanceData: [
      { date: '2024-03-01', clicks: 4000 },
      { date: '2024-03-08', clicks: 6200 },
      { date: '2024-03-15', clicks: 5800 },
      { date: '2024-03-22', clicks: 7300 },
      { date: '2024-03-29', clicks: 8100 }
    ]
  };

  return (
    <div className="min-h-screen bg-navy-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
       

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard 
            icon={MousePointerClick} 
            title="Total Clicks" 
            value={metrics.totalClicks.toLocaleString()}
            color="bg-blue-100"
          />
          <MetricCard 
            icon={Globe} 
            title="Impressions" 
            value={metrics.totalImpressions.toLocaleString()}
            color="bg-green-100"
          />
          <MetricCard 
            icon={BarChart} 
            title="Avg CTR" 
            value={`${metrics.averageCTR}%`}
            color="bg-purple-100"
          />
          <MetricCard 
            icon={Wallet} 
            title="Total Budget" 
            value={`$${metrics.totalBudget.toLocaleString()}`}
            color="bg-orange-100"
          />
        </div>

        {/* Gráficos principales */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Rendimiento de clics */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
            <h3 className="text-xl font-semibold text-navy-900 mb-4">Clicks Performance</h3>
            <div className="h-64">
              <LineChart
                data={metrics.performanceData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#001f3f" 
                  strokeWidth={2}
                />
              </LineChart>
            </div>
          </div>

          {/* Distribución de dispositivos */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
            <h3 className="text-xl font-semibold text-navy-900 mb-4">Device Distribution</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="h-48 flex items-center justify-center">
                <PieChart width={200} height={200}>
                  <Pie
                    data={metrics.deviceDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#1e3a8a"
                  />
                  <Tooltip />
                </PieChart>
              </div>
              <div className="space-y-4">
                {metrics.deviceDistribution.map((device, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      device.name === 'Mobile' ? 'bg-blue-100' : 
                      device.name === 'Desktop' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {device.name === 'Mobile' ? <Smartphone size={20} className="text-navy-900" /> :
                       device.name === 'Desktop' ? <Monitor size={20} className="text-navy-900" /> :
                       <Bot size={20} className="text-navy-900" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="text-navy-900">{device.name}</span>
                        <span className="font-semibold text-navy-900">{device.value}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-navy-900" 
                          style={{ width: `${device.value}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sección inferior */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Campañas activas */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
            <h3 className="text-xl font-semibold text-navy-900 mb-4">Active Campaigns</h3>
            <div className="space-y-4">
              {[...Array(metrics.activeCampaigns)].map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-navy-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-navy-900 text-white p-2 rounded-lg">
                      <Zap size={18} />
                    </div>
                    <span className="text-navy-900">Campaign {index + 1}</span>
                  </div>
                  <div className="text-sm text-navy-700">${(metrics.totalBudget / metrics.activeCampaigns).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement Rate */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
            <h3 className="text-xl font-semibold text-navy-900 mb-4">Engagement Metrics</h3>
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <div className="text-4xl font-bold text-navy-900">
                {metrics.engagementRate}%
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-navy-900" 
                  style={{ width: `${metrics.engagementRate}%` }}
                />
              </div>
              <div className="text-sm text-navy-700">Avg. Engagement Rate</div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
            <h3 className="text-xl font-semibold text-navy-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <StatItem label="Best Performing Ad" value="Summer Sale" />
              <StatItem label="Top Country" value="United States" />
              <StatItem label="Preferred Device" value="Mobile" />
              <StatItem label="Avg. Session Duration" value="2.4m" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Componentes auxiliares
const MetricCard = ({ icon: Icon, title, value, color }) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm border border-navy-100">
    <div className="flex items-center space-x-3">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={24} className="text-navy-900" />
      </div>
      <div>
        <div className="text-sm text-navy-700">{title}</div>
        <div className="text-2xl font-bold text-navy-900">{value}</div>
      </div>
    </div>
  </div>
);

const StatItem = ({ label, value }) => (
  <div className="flex justify-between items-center p-3 bg-navy-50 rounded-lg">
    <span className="text-navy-700">{label}</span>
    <span className="font-semibold text-navy-900">{value}</span>
  </div>
);